import { useEffect, useState } from "react";
import {
  Building2,
  Search,
  UserCog,
} from "lucide-react";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";

import Modal from "../components/common/Modal";
import Loading from "../components/common/LoaderCommon";
import {
  useAssignStaffToBranch,
  useGetAllBranches,
  useGetCounters,
} from "../service/branchService";
import { useGetAllUsers } from "../service/userService";
import { Button } from "../utils/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../utils/components/ui/select";

type AssignmentMap = Record<string, string>;

const STORAGE_KEY = "staff-branch-assignments";

function StaffManagement() {
  const queryClient = useQueryClient();
  const [searchKey, setSearchKey] = useState("");
  const [debouncedSearchKey] = useDebounce(searchKey, 500);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<any | null>(null);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [selectedCounterId, setSelectedCounterId] = useState("");
  const [branchAssignments, setBranchAssignments] = useState<AssignmentMap>({});

  const { data: userData, isLoading: isUsersLoading } = useGetAllUsers({
    userName: debouncedSearchKey,
    pageNo: 0,
    pageSize: 50,
  });

  const { data: branchData, isLoading: isBranchesLoading } = useGetAllBranches({
    params: {
      search: "",
      isActive: true,
      page: 0,
      size: 100,
      sortBy: "branchName",
      sortDir: "asc",
    },
  });

  const { data: counterData, isLoading: isCountersLoading } = useGetCounters({
    params: {
      branchId: selectedBranchId,
      search: "",
      isActive: true,
      page: 0,
      size: 100,
      sortBy: "counterName",
      sortDir: "asc",
    },
  });

  const { mutate: assignStaffToBranch, isPending: isAssigningBranch } =
    useAssignStaffToBranch();

  useEffect(() => {
    const savedAssignments = localStorage.getItem(STORAGE_KEY);
    if (savedAssignments) {
      setBranchAssignments(JSON.parse(savedAssignments));
    }
  }, []);

  const staffs = userData?.userDetailsResponse || [];
  const branches = branchData?.content || [];
  const counters = counterData?.content || [];

  const getRoleBadgeClassName = (role: string) => {
    switch (role) {
      case "SUPER_ADMIN":
        return "border-[#FDE7C7] bg-[#FFF7ED] text-[#C2410C]";
      case "ADMIN":
        return "border-[#DCFCE7] bg-[#F0FDF4] text-[#15803D]";
      default:
        return "border-[#DBEAFE] bg-[#EFF6FF] text-[#1D4ED8]";
    }
  };

  const getAssignedBranchName = (staffId: number) => {
    const assignedBranchId = branchAssignments[String(staffId)];
    const assignedBranch = branches.find(
      (branch) => String(branch.branchId) === assignedBranchId,
    );

    return assignedBranch ? assignedBranch.branchName : "Not assigned";
  };

  const handleOpenAssignModal = (staff: any) => {
    setSelectedStaff(staff);
    setSelectedBranchId(branchAssignments[String(staff.id)] || "");
    setSelectedCounterId("");
    setIsModalOpen(true);
  };

  const handleAssignBranch = () => {
    if (!selectedStaff || !selectedBranchId || !selectedCounterId) {
      toast.error("Please select branch and counter");
      return;
    }

    assignStaffToBranch(
      {
        branchId: Number(selectedBranchId),
        staffId: Number(selectedStaff.id),
        counterId: Number(selectedCounterId),
      },
      {
        onSuccess: () => {
          const updatedAssignments = {
            ...branchAssignments,
            [String(selectedStaff.id)]: selectedBranchId,
          };

          setBranchAssignments(updatedAssignments);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedAssignments));
          queryClient.invalidateQueries({ queryKey: ["useGetAllBranches"] });
          queryClient.invalidateQueries({ queryKey: ["useGetAllUsers"] });
          toast.success(`Branch assigned to ${selectedStaff.username}`);
          setIsModalOpen(false);
          setSelectedStaff(null);
          setSelectedBranchId("");
          setSelectedCounterId("");
        },
        onError: (error: Error) => {
          toast.error(`Failed to assign branch: ${error.message}`);
        },
      },
    );
  };

  if (isUsersLoading || isBranchesLoading) {
    return <Loading />;
  }

  return (
    <>
      <div className="min-h-full py-1">
        <div className="space-y-6">
          <div className="rounded-[26px] border border-[#E8EEF9] bg-white px-6 py-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h1 className="text-[34px] font-semibold tracking-[-0.03em] text-[#1A5EF9]">
                  Staff Management
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-[#7D8998]">
                  Keep branch assignments organized with a clean staff
                  directory, real-time branch visibility, and quick actions
                  for every team member.
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-[26px] border border-[#E8EEF9] bg-white p-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative min-w-[300px] max-w-[460px] flex-1">
                <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#91A0B8]" />
                <input
                  type="text"
                  value={searchKey}
                  onChange={(e) => setSearchKey(e.target.value)}
                  placeholder="Search staff by name..."
                  className="h-12 w-full rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] pl-11 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
              <div className="rounded-[14px] bg-[#F5F8FD] px-4 py-3 text-sm text-[#7D8998] whitespace-nowrap">
                <span className="font-semibold text-[#253650]">
                  {staffs.length}
                </span>{" "}
                staff members found
              </div>
            </div>
          </div>

          <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
            <div className="flex flex-col gap-4 border-b border-[#E8EEF9] px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[#21314A]">
                  Staff List
                </h2>
                <p className="mt-1 text-sm text-[#7D8998]">
                  Assign users to branches and counters.
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-[920px] w-full">
                <thead>
                  <tr className="bg-[#F5F8FD] text-left text-sm font-semibold text-[#31425E]">
                    <th className="px-6 py-4">Staff</th>
                    <th className="min-w-[240px] px-6 py-4">Email</th>
                    <th className="min-w-[160px] px-6 py-4">Role</th>
                    <th className="min-w-[220px] px-6 py-4">Assigned Branch</th>
                    <th className="min-w-[180px] px-6 py-4 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EEF2F6]">
                {staffs.length > 0 ? (
                  staffs.map((staff) => (
                    <tr
                      key={staff.id}
                      className="transition hover:bg-[#FAFCFF]"
                    >
                      <td className="px-6 py-5 align-middle">
                        <div className="flex items-center gap-3">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#E8F0FF] text-[#1A5EF9] ring-1 ring-[#CFE0FF]">
                            <UserCog className="h-5 w-5" />
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-medium text-slate-900">
                              {staff.username}
                            </p>
                            <p className="truncate text-sm text-slate-500">
                              {staff.phoneNumber || "No phone number"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5 align-middle text-sm text-slate-600 break-all">
                        {staff.email}
                      </td>
                      <td className="px-6 py-5 align-middle">
                        <span
                          className={`inline-flex rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-wide ${getRoleBadgeClassName(
                            staff.role,
                          )}`}
                        >
                          {staff.role}
                        </span>
                      </td>
                      <td className="px-6 py-5 align-middle">
                        <div className="inline-flex items-center gap-2 rounded-full border border-[#E6ECF5] bg-[#F8FAFC] px-3 py-1.5 text-sm text-slate-600">
                          <Building2 className="h-4 w-4 shrink-0 text-slate-400" />
                          {getAssignedBranchName(staff.id)}
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center align-middle">
                        <Button
                          type="button"
                          onClick={() => handleOpenAssignModal(staff)}
                          className="min-w-[170px] rounded-xl bg-[#1A5EF9] text-white shadow-[0_14px_24px_-18px_rgba(26,94,249,0.95)] hover:bg-[#0F4ED8]"
                        >
                          {branchAssignments[String(staff.id)]
                            ? "Change Branch"
                            : "Assign Branch"}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-12 text-center text-sm text-slate-500"
                    >
                      No staff members found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <Modal
        open={isModalOpen}
        setOpen={(open) => {
          setIsModalOpen(open);
          if (!open) {
            setSelectedStaff(null);
            setSelectedBranchId("");
            setSelectedCounterId("");
          }
        }}
      >
        <div className="w-[420px] max-w-[92vw] rounded-xl bg-white p-2">
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-slate-900">
              Assign Branch
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Select a branch for{" "}
              {selectedStaff?.username || "this staff member"}.
            </p>
          </div>

          <div className="space-y-4">
            <div className="rounded-xl border border-[#E8EDF3] bg-[#F8FAFC] p-4">
              <p className="text-xs uppercase tracking-wide text-slate-400">
                Staff
              </p>
              <p className="mt-1 font-medium text-slate-900">
                {selectedStaff?.username}
              </p>
              <p className="text-sm text-slate-500">{selectedStaff?.email}</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Branch
              </label>
              <Select
                value={selectedBranchId}
                onValueChange={(value) => {
                  setSelectedBranchId(value);
                  setSelectedCounterId("");
                }}
              >
                <SelectTrigger className="h-11 w-full rounded-xl border-[#D8E1EC] bg-white">
                  <SelectValue placeholder="Select a branch" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem
                      key={branch.branchId}
                      value={String(branch.branchId)}
                    >
                      {branch.branchName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Counter
              </label>
              <Select
                value={selectedCounterId}
                onValueChange={(value) => setSelectedCounterId(value)}
                disabled={!selectedBranchId || isCountersLoading}
              >
                <SelectTrigger className="h-11 w-full rounded-xl border-[#D8E1EC] bg-white">
                  <SelectValue
                    placeholder={
                      !selectedBranchId
                        ? "Select a branch first"
                        : isCountersLoading
                          ? "Loading counters..."
                          : counters.length > 0
                            ? "Select a counter"
                            : "No counters found"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {counters.map((counter) => (
                    <SelectItem key={counter.id} value={String(counter.id)}>
                      {counter.counterName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                disabled={isAssigningBranch}
                onClick={() => {
                  setIsModalOpen(false);
                  setSelectedStaff(null);
                  setSelectedBranchId("");
                  setSelectedCounterId("");
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleAssignBranch}
                disabled={isAssigningBranch}
                className="bg-[#1A5EF9] text-white hover:bg-[#0F4ED8]"
              >
                {isAssigningBranch ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default StaffManagement;
